import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Bot,
  CheckCircle2,
  Clock3,
  Gauge,
  RefreshCw,
  ShieldCheck,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { ClaraCard } from '../layout/ClaraLayoutShell';
import { masterSupabase } from '../../lib/supabase-master.js';

const Metric = ({ label, value, hint, tone = 'neutral' }) => {
  const toneClasses = {
    neutral: 'border-gray-200 bg-gray-50 text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100',
    good: 'border-green-200 bg-green-50 text-green-900 dark:border-green-900/60 dark:bg-green-950/30 dark:text-green-200',
    warning: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200',
    danger: 'border-red-200 bg-red-50 text-red-900 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200',
    blue: 'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900/60 dark:bg-blue-950/30 dark:text-blue-200',
  };

  return (
    <div className={`rounded-lg border p-3 ${toneClasses[tone] || toneClasses.neutral}`}>
      <div className="text-xl font-bold tabular-nums">{value}</div>
      <div className="mt-1 text-xs font-medium">{label}</div>
      {hint && <div className="mt-1 text-[11px] opacity-70">{hint}</div>}
    </div>
  );
};

const ShadowProactivityWidget = () => {
  const [report, setReport] = useState(null);
  const [operations, setOperations] = useState(null);
  const [pilot, setPilot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const { data: { session } } = await masterSupabase.auth.getSession();
      if (!session?.access_token) throw new Error('Keine aktive Clara-Sitzung');
      const headers = {
        Authorization: `Bearer ${session.access_token}`,
        'x-supabase-access-token': session.access_token,
      };
      const [reportResponse, operationsResponse, pilotResponse] = await Promise.all([
        fetch('/api/shadow/report', { headers }),
        fetch('/api/operations/context?limit=1', { headers }),
        fetch('/api/operations/pilot/scorecard', { headers }),
      ]);
      if (!reportResponse.ok) throw new Error(`Shadow Report nicht verfügbar (${reportResponse.status})`);
      if (!operationsResponse.ok) throw new Error(`Operations-Status nicht verfügbar (${operationsResponse.status})`);
      setReport(await reportResponse.json());
      setOperations(await operationsResponse.json());
      setPilot(pilotResponse.ok ? await pilotResponse.json() : null);
    } catch (requestError) {
      console.error('Failed to load Clara Shadow Report:', requestError);
      setError(requestError.message || 'Shadow Report konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load({ quiet: true }), 60000);
    return () => window.clearInterval(timer);
  }, [load]);

  const connector = useMemo(
    () => operations?.connectors?.find((item) => item.connector_id === 'imap:hiss@clara360.de'),
    [operations],
  );

  if (loading) {
    return (
      <ClaraCard title="Clara Shadow Report" icon={<Gauge className="h-5 w-5 text-violet-600" />}>
        <div className="flex h-32 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-violet-600" />
        </div>
      </ClaraCard>
    );
  }

  const autonomyRate = report?.verified_autonomy_rate;
  const rateLabel = autonomyRate === null || autonomyRate === undefined ? '–' : `${Number(autonomyRate).toLocaleString('de-DE')} %`;
  const policyViolations = Number(report?.policy_violations || 0);
  const unresolved = Number(report?.unresolved_events_without_blocker || 0);
  const connectorHealthy = connector?.status === 'healthy';
  const humanCorrectionRate = report?.human_correction_rate;
  const approvalAcceptanceRate = report?.approval_acceptance_rate;
  const humanCorrectionLabel = humanCorrectionRate === null || humanCorrectionRate === undefined ? '–' : `${Number(humanCorrectionRate).toLocaleString('de-DE')} %`;
  const approvalAcceptanceLabel = approvalAcceptanceRate === null || approvalAcceptanceRate === undefined ? '–' : `${Number(approvalAcceptanceRate).toLocaleString('de-DE')} %`;
  const pilotQuality = pilot?.quality || {};
  const pilotProgress = pilot?.progress || {};
  const pilotWork = pilot?.work_removed || {};
  const pilotTiming = pilot?.timing || {};
  const pilotGate = pilot?.gate?.status || 'collecting';
  const percentage = (value) => value === null || value === undefined ? '–' : `${Number(value).toLocaleString('de-DE')} %`;
  const minutes = (value) => value === null || value === undefined ? '–' : `${Number(value).toLocaleString('de-DE')} Min.`;
  const gateLabel = {
    collecting: 'Datenerhebung läuft',
    pass_candidate: 'PASS-Kandidat',
    conditional_candidate: 'CONDITIONAL',
    fail: 'FAIL',
  }[pilotGate] || pilotGate;
  const gateTone = pilotGate === 'pass_candidate' ? 'good' : pilotGate === 'fail' ? 'danger' : pilotGate === 'conditional_candidate' ? 'warning' : 'blue';

  return (
    <ClaraCard title="Clara Shadow Report · letzte 24 Stunden" icon={<Gauge className="h-5 w-5 text-violet-600" />}>
      <div className="p-4">
        {error ? (
          <div className="flex items-start justify-between rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <span>{error}</span>
            <button type="button" onClick={() => load()} className="ml-3 inline-flex items-center font-medium">
              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Erneut laden
            </button>
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-violet-600" />
                  <p className="font-semibold text-gray-900 dark:text-gray-100">Shadow Proactivity ist aktiv</p>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Gemessen wird, was Clara korrekt vorbereitet oder autonom abgeschlossen hätte. Technische Tests und Recovery-Replays sind ausgeschlossen; Korrekturen und Approval-Entscheidungen werden separat ausgewiesen.
                </p>
              </div>
              <div className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${connectorHealthy ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {connectorHealthy ? <Wifi className="mr-1.5 h-3.5 w-3.5" /> : <WifiOff className="mr-1.5 h-3.5 w-3.5" />}
                {connectorHealthy ? 'Mail-Eingang gesund' : 'Mail-Eingang prüfen'}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Metric label="Events erkannt" value={report?.events_recognized || 0} hint="produktive Eingänge" tone="blue" />
              <Metric label="Shadow-Entscheidungen" value={report?.shadow_decisions || 0} hint="messbare Entscheidungen" />
              <Metric label="Entscheidung nötig" value={report?.decisions_required || 0} hint="menschliche Klärung" tone={Number(report?.decisions_required || 0) > 0 ? 'warning' : 'good'} />
              <Metric label="Arbeit vorbereitet" value={report?.prepared_actions || 0} hint="operative Schritte" tone="blue" />
              <Metric label="Zeitersparnis" value={`${Number(report?.predicted_minutes_saved || 0).toLocaleString('de-DE')} Min.`} hint="vorsichtige Schätzung" tone="good" />
              <Metric label="Clara Autonomy Rate" value={rateLabel} hint={`${report?.autonomously_correct || 0} korrekt · ${report?.evaluated || 0} bewertet`} tone={autonomyRate === null ? 'neutral' : Number(autonomyRate) >= 70 ? 'good' : Number(autonomyRate) >= 30 ? 'warning' : 'neutral'} />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Metric label="Human Correction Rate" value={humanCorrectionLabel} hint={`${report?.manual_corrections || 0} Korrekturen · ${report?.quality_evaluations || 0} Qualitätsbewertungen`} tone={humanCorrectionRate === null || humanCorrectionRate === undefined ? 'neutral' : Number(humanCorrectionRate) < 5 ? 'good' : Number(humanCorrectionRate) < 15 ? 'warning' : 'danger'} />
              <Metric label="Approval Acceptance Rate" value={approvalAcceptanceLabel} hint={`${report?.approvals_accepted || 0} angenommen · ${report?.approvals_rejected || 0} abgelehnt`} tone={approvalAcceptanceRate === null || approvalAcceptanceRate === undefined ? 'neutral' : Number(approvalAcceptanceRate) >= 80 ? 'good' : Number(approvalAcceptanceRate) >= 50 ? 'warning' : 'danger'} />
              <Metric label="False Positives" value={report?.false_positives || 0} hint={`${report?.false_positive_rate === null || report?.false_positive_rate === undefined ? '–' : `${Number(report.false_positive_rate).toLocaleString('de-DE')} %`} der Bewertungen`} tone={Number(report?.false_positives || 0) === 0 ? 'good' : 'danger'} />
              <Metric label="False Negatives" value={report?.false_negatives || 0} hint={`${report?.false_negative_rate === null || report?.false_negative_rate === undefined ? '–' : `${Number(report.false_negative_rate).toLocaleString('de-DE')} %`} der Bewertungen`} tone={Number(report?.false_negatives || 0) === 0 ? 'good' : 'danger'} />
            </div>

            <div className="mt-4 grid gap-2 text-sm sm:grid-cols-3">
              <div className="flex items-center rounded-md bg-gray-50 p-2 dark:bg-gray-900">
                <Activity className="mr-2 h-4 w-4 text-blue-500" />
                <span><strong>{report?.duplicate_cases || 0}</strong> echte Duplikate unterdrückt</span>
              </div>
              <div className={`flex items-center rounded-md p-2 ${unresolved === 0 ? 'bg-green-50 text-green-800 dark:bg-green-950/30 dark:text-green-200' : 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200'}`}>
                {unresolved === 0 ? <CheckCircle2 className="mr-2 h-4 w-4" /> : <AlertTriangle className="mr-2 h-4 w-4" />}
                <span><strong>{unresolved}</strong> ungeklärte Events ohne Blocker</span>
              </div>
              <div className={`flex items-center rounded-md p-2 ${policyViolations === 0 ? 'bg-green-50 text-green-800 dark:bg-green-950/30 dark:text-green-200' : 'bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-200'}`}>
                <ShieldCheck className="mr-2 h-4 w-4" />
                <span><strong>{policyViolations}</strong> Policy-Verstöße</span>
              </div>
            </div>

            {pilot && (
              <div className="mt-5 rounded-xl border border-violet-200 bg-violet-50/70 p-4 dark:border-violet-900/60 dark:bg-violet-950/20">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h4 className="font-semibold text-violet-950 dark:text-violet-100">Real-Vacancy-Pilot · reale Fälle</h4>
                    <p className="mt-1 text-xs text-violet-800 dark:text-violet-300">Keine synthetischen Fälle. Touch Time zählt nur serverseitig bestätigte aktive Bearbeitung; externe Wartezeit ist ausgeschlossen.</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${gateTone === 'good' ? 'bg-green-100 text-green-800' : gateTone === 'danger' ? 'bg-red-100 text-red-800' : gateTone === 'warning' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>{gateLabel}</span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                  <Metric label="Pilotfortschritt" value={`${pilotProgress.completed || 0}/${pilotProgress.target_min || 3}`} hint={`${pilotProgress.enrolled || 0} eingeschrieben · max. ${pilotProgress.target_max || 5}`} tone="blue" />
                  <Metric label="Clara Work Removed" value={percentage(pilotWork.work_removed_rate)} hint={`${minutes(pilotWork.work_removed_minutes)} entfernt`} tone={pilotWork.work_removed_rate === null || pilotWork.work_removed_rate === undefined ? 'neutral' : Number(pilotWork.work_removed_rate) >= 70 ? 'good' : 'warning'} />
                  <Metric label="Human Touch Time" value={minutes(pilotWork.human_touch_minutes)} hint={`${minutes(pilotWork.baseline_minutes)} Baseline`} />
                  <Metric label="Decision Ready" value={minutes(pilotTiming.median_decision_ready_minutes)} hint="Median ab Eingang" />
                  <Metric label="Materielle Korrekturen" value={percentage(pilotQuality.material_human_correction_rate)} hint={`${pilotQuality.materially_corrected_cases || 0}/${pilotQuality.assessed_cases || 0} Fälle`} tone={pilotQuality.material_human_correction_rate === null || pilotQuality.material_human_correction_rate === undefined ? 'neutral' : Number(pilotQuality.material_human_correction_rate) <= 10 ? 'good' : 'danger'} />
                  <Metric label="First-Pass Approval" value={percentage(pilotQuality.first_pass_approval_rate)} hint={`${pilotQuality.first_pass_accepted || 0}/${pilotQuality.proposal_decisions || 0} Vorschläge`} tone={pilotQuality.first_pass_approval_rate === null || pilotQuality.first_pass_approval_rate === undefined ? 'neutral' : Number(pilotQuality.first_pass_approval_rate) >= 80 ? 'good' : 'warning'} />
                </div>
                <div className="mt-3 grid gap-2 text-xs sm:grid-cols-4">
                  <div className="rounded-md bg-white/80 p-2 dark:bg-gray-900/70"><strong>Entity Resolution:</strong> {percentage(pilotQuality.entity_resolution_accuracy)}</div>
                  <div className="rounded-md bg-white/80 p-2 dark:bg-gray-900/70"><strong>Evidence beim Erstreview:</strong> {percentage(pilotQuality.evidence_completeness_at_first_review)}</div>
                  <div className="rounded-md bg-white/80 p-2 dark:bg-gray-900/70"><strong>False Negatives:</strong> {pilotQuality.false_negatives || 0} aus {pilotQuality.control_messages_reviewed || 0} kontrollierten Mails</div>
                  <div className={`rounded-md p-2 ${(pilotQuality.silent_misassignments || 0) === 0 && (pilotQuality.policy_violations || 0) === 0 && (pilotQuality.external_without_approval || 0) === 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}><strong>Hard Gates:</strong> {pilotQuality.silent_misassignments || 0} still falsch · {pilotQuality.policy_violations || 0} Policy · {pilotQuality.external_without_approval || 0} extern ohne Approval</div>
                </div>
              </div>
            )}

            <div className="mt-3 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
              <span className="inline-flex items-center"><Clock3 className="mr-1 h-3 w-3" /> Stand {new Date(report.generated_at).toLocaleString('de-DE')}</span>
              <span>Autonomie und Work Removed werden durch reale Clara-Evidence verdient.</span>
            </div>
          </>
        )}
      </div>
    </ClaraCard>
  );
};

export default ShadowProactivityWidget;
